import { useCallback, useState } from 'react';

/**
 * Estado do assistente em etapas: em qual a pessoa esta e para onde pode ir.
 *
 * Fica fora do Stepper porque o indicador so desenha; quem decide se a etapa atual
 * pode avancar (campos obrigatorios, resposta do servidor) e o formulario, que chama
 * `next` depois de conferir. Voltar nunca perde o que foi digitado: o estado dos
 * campos e do formulario, e nao da etapa.
 */
export function useStepper<K extends string>(keys: readonly [K, ...K[]], initial?: K) {
  const [index, setIndex] = useState(() => {
    const inicial = initial === undefined ? 0 : keys.indexOf(initial);
    return inicial < 0 ? 0 : inicial;
  });
  const limite = keys.length - 1;
  const atual = Math.min(index, limite);

  const next = useCallback(() => setIndex((i) => Math.min(i + 1, limite)), [limite]);
  const back = useCallback(() => setIndex((i) => Math.max(i - 1, 0)), []);
  const goTo = useCallback(
    (key: K) => {
      const destino = keys.indexOf(key);
      if (destino >= 0) setIndex(destino);
    },
    [keys],
  );
  const reset = useCallback(() => setIndex(0), []);

  return {
    index: atual,
    current: keys[atual] ?? keys[0],
    isFirst: atual === 0,
    isLast: atual === limite,
    next,
    back,
    goTo,
    reset,
  };
}
